/**
 * Affiliate / Creator Program — internal admin dashboard.
 *
 * Create & manage creators (codes, OneLinks, per-creator negotiated terms),
 * inspect the commission ledger, and run payouts (approve past the hold, then
 * mark paid in a batch). Matches the /metrics analytics design system.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  StatCard,
  ChartCard,
  DataTable,
  Segmented,
  LoadingState,
  ErrorState,
} from "../Analytics/design/ui";
import type { Column } from "../Analytics/design/ui";
import "../Analytics/design/tokens.css";
import "../Analytics/design/ui/ui.css";
import "../Analytics/design/dashboard.css";
import "./AffiliateDashboard.css";
import {
  Creator,
  Commission,
  CommissionStatus,
  PayoutOwed,
  CommissionTerms,
  NewCreatorPayload,
  listCreators,
  getLedger,
  getPayoutsOwed,
  createCreator,
  updateCreator,
  approveCommissions,
  markPaid,
  voidCommission,
  centsToUsd,
} from "./affiliateApi";

type Tab = "creators" | "ledger" | "payouts";

const STATUS_BADGE: Record<CommissionStatus, string> = {
  pending: "an-badge an-badge--warning",
  approved: "an-badge an-badge--accent",
  paid: "an-badge an-badge--positive",
  void: "an-badge an-badge--negative",
};

const copy = (text: string) => {
  navigator.clipboard?.writeText(text).catch(() => {});
};

const AffiliateDashboard: React.FC = () => {
  const [tab, setTab] = useState<Tab>("creators");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [creators, setCreators] = useState<Creator[]>([]);
  const [defaults, setDefaults] = useState<CommissionTerms | null>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [payouts, setPayouts] = useState<PayoutOwed[]>([]);

  const [statusFilter, setStatusFilter] = useState<CommissionStatus | "all">("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Creator | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, l, p] = await Promise.all([listCreators(), getLedger(), getPayoutsOwed()]);
      setCreators(c.creators || []);
      setDefaults(c.defaults);
      setCommissions(l.commissions || []);
      setPayouts(p.payouts || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load affiliate data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // KPIs from the ledger
  const kpis = useMemo(() => {
    const totals = { pending: 0, approved: 0, paid: 0, void: 0 } as Record<
      CommissionStatus,
      number
    >;
    commissions.forEach((c) => {
      totals[c.status] = (totals[c.status] || 0) + (c.amountCents || 0);
    });
    const owed = payouts.reduce((s, p) => s + p.owedCents, 0);
    return {
      creators: creators.length,
      active: creators.filter((c) => c.status === "active").length,
      pending: totals.pending,
      owed,
      paid: totals.paid,
    };
  }, [commissions, payouts, creators]);

  const creatorName = useCallback(
    (id: string) => creators.find((c) => c.creatorId === id)?.name || id,
    [creators]
  );

  const filteredCommissions = useMemo(
    () =>
      statusFilter === "all" ? commissions : commissions.filter((c) => c.status === statusFilter),
    [commissions, statusFilter]
  );

  const handleApprove = async () => {
    setBusy(true);
    try {
      const r = await approveCommissions();
      alert(`Approved ${r.approved} commission(s) past their hold period.`);
      await load();
    } catch (e: any) {
      alert(`Approve failed: ${e?.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPaid = async (p: PayoutOwed) => {
    if (
      !window.confirm(
        `Mark ${centsToUsd(p.owedCents)} as paid to ${p.name}? Do this AFTER you've sent the payout.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const r = await markPaid(p.creatorId);
      alert(
        `Marked ${r.paid} commission(s) = ${centsToUsd(r.paidCents)} paid (batch ${r.payoutBatchId}).`
      );
      await load();
    } catch (e: any) {
      alert(`Mark paid failed: ${e?.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleVoid = async (c: Commission) => {
    const reason = window.prompt(
      `Void this ${c.type} commission (${centsToUsd(c.amountCents)})? Enter a reason:`
    );
    if (reason == null) return;
    setBusy(true);
    try {
      await voidCommission(c.commissionId, reason);
      await load();
    } catch (e: any) {
      alert(`Void failed: ${e?.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleTogglePause = async (c: Creator) => {
    setBusy(true);
    try {
      await updateCreator({
        creatorId: c.creatorId,
        status: c.status === "active" ? "paused" : "active",
      });
      await load();
    } catch (e: any) {
      alert(`Update failed: ${e?.message}`);
    } finally {
      setBusy(false);
    }
  };

  // ── column defs ────────────────────────────────────────────────────────────
  const creatorColumns: Column<Creator>[] = [
    {
      key: "name",
      header: "Creator",
      render: (c) => (
        <div>
          <div className="af-strong">{c.name}</div>
          <div className="af-muted">{c.email}</div>
        </div>
      ),
    },
    {
      key: "referralCode",
      header: "Code",
      render: (c) => (
        <button className="af-chip" title="Copy code" onClick={() => copy(c.referralCode)}>
          {c.referralCode} ⧉
        </button>
      ),
    },
    {
      key: "discountPercent",
      header: "Discount",
      align: "right",
      render: (c) => (c.discountPercent ? `${c.discountPercent}%` : "—"),
    },
    {
      key: "signup",
      header: "Signup",
      align: "right",
      sortValue: (c) => c.terms.signupBountyCents,
      render: (c) => centsToUsd(c.terms.signupBountyCents),
    },
    {
      key: "pct",
      header: "1st pmt",
      align: "right",
      sortValue: (c) => c.terms.firstPaymentPct,
      render: (c) => `${c.terms.firstPaymentPct}%`,
    },
    {
      key: "status",
      header: "Status",
      render: (c) => (
        <span className={c.status === "active" ? "an-badge an-badge--positive" : "an-badge"}>
          {c.status}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (c) => (
        <div className="af-actions">
          {c.oneLinkUrl && (
            <button className="an-button" onClick={() => copy(c.oneLinkUrl!)}>
              Copy link
            </button>
          )}
          <button
            className="an-button"
            onClick={() => {
              setEditing(c);
              setShowForm(true);
            }}
          >
            Edit
          </button>
          <button className="an-button" onClick={() => handleTogglePause(c)} disabled={busy}>
            {c.status === "active" ? "Pause" : "Activate"}
          </button>
        </div>
      ),
    },
  ];

  const commissionColumns: Column<Commission>[] = [
    { key: "creatorId", header: "Creator", render: (c) => creatorName(c.creatorId) },
    {
      key: "userEmail",
      header: "User",
      render: (c) => <span className="af-muted">{c.userEmail}</span>,
    },
    { key: "type", header: "Type" },
    {
      key: "amountCents",
      header: "Amount",
      align: "right",
      render: (c) => centsToUsd(c.amountCents),
    },
    {
      key: "status",
      header: "Status",
      render: (c) => <span className={STATUS_BADGE[c.status]}>{c.status}</span>,
    },
    {
      key: "availableAt",
      header: "Payable after",
      render: (c) => (c.availableAt ? c.availableAt.slice(0, 10) : "—"),
    },
    { key: "createdAt", header: "Created", render: (c) => c.createdAt.slice(0, 10) },
    {
      key: "actions",
      header: "",
      render: (c) =>
        c.status === "pending" || c.status === "approved" ? (
          <button className="an-button" onClick={() => handleVoid(c)} disabled={busy}>
            Void
          </button>
        ) : null,
    },
  ];

  const payoutColumns: Column<PayoutOwed>[] = [
    {
      key: "name",
      header: "Creator",
      render: (p) => (
        <div>
          <div className="af-strong">{p.name}</div>
          <div className="af-muted">{p.email}</div>
        </div>
      ),
    },
    {
      key: "payoutHandle",
      header: "Pay to",
      render: (p) => (
        <span className="af-muted">
          {p.payoutMethod || "—"}
          {p.payoutHandle ? ` · ${p.payoutHandle}` : ""}
        </span>
      ),
    },
    {
      key: "owedCents",
      header: "Owed",
      align: "right",
      render: (p) => <span className="af-strong">{centsToUsd(p.owedCents)}</span>,
    },
    {
      key: "readyToPay",
      header: "Min met?",
      render: (p) =>
        p.readyToPay ? (
          <span className="an-badge an-badge--positive">yes</span>
        ) : (
          <span className="an-badge an-badge--warning">below {centsToUsd(p.minPayoutCents)}</span>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (p) => (
        <button
          className="an-button an-button--primary"
          onClick={() => handleMarkPaid(p)}
          disabled={busy}
        >
          Mark paid
        </button>
      ),
    },
  ];

  return (
    <div className="an-root">
      <div className="an-shell">
        <div className="an-header">
          <div>
            <h1 className="an-header-title">Affiliate Program</h1>
            <p className="an-header-sub">Manage creators, track commissions, and run payouts.</p>
          </div>
          <button
            className="an-button an-button--primary"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            + New creator
          </button>
        </div>

        <div className="an-stat-grid af-kpis">
          <StatCard label="Creators" value={kpis.creators} sub={`${kpis.active} active`} />
          <StatCard
            label="Pending"
            value={centsToUsd(kpis.pending)}
            sub="Awaiting hold / approval"
          />
          <StatCard label="Owed (approved)" value={centsToUsd(kpis.owed)} sub="Ready to pay out" />
          <StatCard label="Paid to date" value={centsToUsd(kpis.paid)} sub="Lifetime" />
        </div>

        <nav className="an-tabs af-tabs">
          {(["creators", "ledger", "payouts"] as Tab[]).map((t) => (
            <button
              key={t}
              className={`an-tab ${tab === t ? "an-tab--active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t === "creators" ? "Creators" : t === "ledger" ? "Ledger" : "Payouts"}
            </button>
          ))}
        </nav>

        <div className="an-body">
          {loading ? (
            <LoadingState message="Loading affiliate data…" />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : tab === "creators" ? (
            <ChartCard title="Creators" subtitle={`${creators.length} total`}>
              <DataTable
                columns={creatorColumns}
                rows={creators}
                rowKey={(c) => c.creatorId}
                initialSortKey="name"
              />
            </ChartCard>
          ) : tab === "ledger" ? (
            <ChartCard
              title="Commission ledger"
              subtitle={`${filteredCommissions.length} shown`}
              actions={
                <button
                  className="an-button an-button--primary"
                  onClick={handleApprove}
                  disabled={busy}
                >
                  Approve eligible (past hold)
                </button>
              }
            >
              <div className="af-filter">
                <Segmented
                  options={["all", "pending", "approved", "paid", "void"].map((s) => ({
                    label: s,
                    value: s,
                  }))}
                  value={statusFilter}
                  onChange={(v) => setStatusFilter(v as CommissionStatus | "all")}
                />
              </div>
              <DataTable
                columns={commissionColumns}
                rows={filteredCommissions}
                rowKey={(c) => c.commissionId}
                initialSortKey="createdAt"
                pageSize={25}
              />
            </ChartCard>
          ) : (
            <ChartCard title="Payouts owed" subtitle="Approved but unpaid, grouped by creator">
              {payouts.length === 0 ? (
                <p className="af-muted af-empty">
                  Nothing owed right now. Approve eligible commissions in the Ledger tab first.
                </p>
              ) : (
                <DataTable
                  columns={payoutColumns}
                  rows={payouts}
                  rowKey={(p) => p.creatorId}
                  initialSortKey="owedCents"
                />
              )}
            </ChartCard>
          )}
        </div>
      </div>

      {showForm && (
        <CreatorForm
          creator={editing}
          defaults={defaults}
          onClose={() => setShowForm(false)}
          onSaved={async () => {
            setShowForm(false);
            await load();
          }}
        />
      )}
    </div>
  );
};

// ── Create / edit creator form (modal) ────────────────────────────────────────
const CreatorForm: React.FC<{
  creator: Creator | null;
  defaults: CommissionTerms | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ creator, defaults, onClose, onSaved }) => {
  const t = creator?.terms ||
    defaults || {
      signupBountyCents: 50,
      firstPaymentPct: 30,
      attributionWindowDays: 30,
      holdDays: 30,
      minPayoutCents: 5000,
      currency: "USD",
    };
  const [form, setForm] = useState({
    name: creator?.name || "",
    email: creator?.email || "",
    referralCode: creator?.referralCode || "",
    discountPercent: creator?.discountPercent ?? 0,
    oneLinkUrl: creator?.oneLinkUrl || "",
    payoutMethod: creator?.payoutMethod || "paypal",
    payoutHandle: creator?.payoutHandle || "",
    notes: creator?.notes || "",
    signupBountyUsd: (t.signupBountyCents / 100).toFixed(2),
    firstPaymentPct: t.firstPaymentPct,
    attributionWindowDays: t.attributionWindowDays,
    holdDays: t.holdDays,
    minPayoutUsd: (t.minPayoutCents / 100).toFixed(2),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const terms: Partial<CommissionTerms> = {
        signupBountyCents: Math.round(parseFloat(form.signupBountyUsd) * 100) || 0,
        firstPaymentPct: Number(form.firstPaymentPct) || 0,
        attributionWindowDays: Number(form.attributionWindowDays) || 30,
        holdDays: Number(form.holdDays) || 0,
        minPayoutCents: Math.round(parseFloat(form.minPayoutUsd) * 100) || 0,
      };
      const base = {
        name: form.name,
        email: form.email,
        referralCode: form.referralCode || undefined,
        discountPercent: Number(form.discountPercent) || 0,
        oneLinkUrl: form.oneLinkUrl || undefined,
        payoutMethod: form.payoutMethod,
        payoutHandle: form.payoutHandle || undefined,
        notes: form.notes || undefined,
        terms,
      };
      if (creator) {
        await updateCreator({ creatorId: creator.creatorId, ...base } as any);
      } else {
        await createCreator(base as NewCreatorPayload);
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="af-modal-overlay" onClick={onClose}>
      <div className="af-modal an-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="an-card-title">{creator ? `Edit ${creator.name}` : "New creator"}</h2>
        <div className="af-form-grid">
          <label>
            Name
            <input value={form.name} onChange={(e) => set("name", e.target.value)} />
          </label>
          <label>
            Email
            <input value={form.email} onChange={(e) => set("email", e.target.value)} />
          </label>
          <label>
            Referral code
            <input
              value={form.referralCode}
              placeholder="auto"
              onChange={(e) => set("referralCode", e.target.value.toUpperCase())}
            />
          </label>
          <label>
            Audience discount %
            <input
              type="number"
              value={form.discountPercent}
              onChange={(e) => set("discountPercent", e.target.value)}
            />
          </label>
          <label className="af-span2">
            OneLink URL
            <input
              value={form.oneLinkUrl}
              placeholder="https://chatterbox.onelink.me/..."
              onChange={(e) => set("oneLinkUrl", e.target.value)}
            />
          </label>
          <label>
            Signup bounty ($)
            <input
              type="number"
              step="0.01"
              value={form.signupBountyUsd}
              onChange={(e) => set("signupBountyUsd", e.target.value)}
            />
          </label>
          <label>
            First payment %
            <input
              type="number"
              value={form.firstPaymentPct}
              onChange={(e) => set("firstPaymentPct", e.target.value)}
            />
          </label>
          <label>
            Attribution window (days)
            <input
              type="number"
              value={form.attributionWindowDays}
              onChange={(e) => set("attributionWindowDays", e.target.value)}
            />
          </label>
          <label>
            Hold period (days)
            <input
              type="number"
              value={form.holdDays}
              onChange={(e) => set("holdDays", e.target.value)}
            />
          </label>
          <label>
            Min payout ($)
            <input
              type="number"
              step="0.01"
              value={form.minPayoutUsd}
              onChange={(e) => set("minPayoutUsd", e.target.value)}
            />
          </label>
          <label>
            Payout method
            <input
              value={form.payoutMethod}
              onChange={(e) => set("payoutMethod", e.target.value)}
            />
          </label>
          <label className="af-span2">
            Payout handle
            <input
              value={form.payoutHandle}
              placeholder="PayPal email / handle"
              onChange={(e) => set("payoutHandle", e.target.value)}
            />
          </label>
          <label className="af-span2">
            Notes
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </label>
        </div>
        {err && <p className="af-error">{err}</p>}
        <div className="af-modal-actions">
          <button className="an-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="an-button an-button--primary"
            onClick={submit}
            disabled={saving || !form.name || !form.email}
          >
            {saving ? "Saving…" : creator ? "Save changes" : "Create creator"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
